import { ArrowRight, Check, Loader2, Sparkles } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { firstMeaningfulWord } from '@/lib/shop-name';

/**
 * Welcome-page fallback plan-picker for shops that arrived without a
 * `?plan=starter|pro` param on the marketing pricing card (NEA-6203). Rendered
 * only when `shop.plan === 'unknown-plan'` and the shop is not migration
 * cohort — every other case is handled by the normal ReadyToLaunchHero path.
 *
 * On pick, calls `onSelect(plan)` — the Welcome container fires
 * `subscribe(plan)` then `dismiss()` and navigates to the dashboard.
 */
export function WelcomePlanPicker({ name, onSelect, loading }) {
	const firstName = firstMeaningfulWord(name);
	return (
		<div className="w-full max-w-[820px] text-center">
			<div className="mb-6 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
				<Sparkles className="h-3.5 w-3.5" /> Pick your plan
			</div>
			<h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight md:text-5xl">
				One last thing
				{firstName ? (
					<>
						, <span className="text-gradient-unify">{firstName}</span>
					</>
				) : null}
			</h1>
			<p className="mx-auto mt-5 max-w-[560px] text-lg text-muted-foreground">
				Choose the plan that fits your store. You can change it any time from Billing.
			</p>

			<div className="mt-10 grid gap-5 md:grid-cols-2">
				<PlanCard
					plan="starter"
					title="Starter"
					blurb="Get your in-store products live on Google and your Unify storefront."
					features={['Google Local Listings', 'Unify storefront', 'One paid channel add-on']}
					onSelect={onSelect}
					loading={loading}
				/>
				<PlanCard
					plan="pro"
					title="Pro"
					blurb="Everything in Starter plus two channels included and priority support."
					features={['Everything in Starter', 'Two channel add-ons included', 'Priority support']}
					highlight
					onSelect={onSelect}
					loading={loading}
				/>
			</div>
		</div>
	);
}

function PlanCard({ plan, title, blurb, features, highlight, onSelect, loading }) {
	return (
		<Card className={`p-6 text-left ${highlight ? 'border-primary/40 shadow-[0_20px_40px_-16px_rgba(79,70,229,0.35)]' : ''}`}>
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-extrabold tracking-tight">{title}</h2>
				{highlight ? (
					<span className="rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">
						Recommended
					</span>
				) : null}
			</div>
			<p className="mt-2 text-sm text-muted-foreground">{blurb}</p>
			<ul className="mt-5 space-y-2 text-sm">
				{features.map((f) => (
					<li key={f} className="flex items-start gap-2">
						<Check className="mt-0.5 h-4 w-4 flex-none text-primary" aria-hidden />
						<span>{f}</span>
					</li>
				))}
			</ul>
			<button
				type="button"
				onClick={() => onSelect(plan)}
				disabled={loading}
				className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-unify px-6 py-3 text-sm font-bold text-primary-foreground shadow-[0_10px_24px_-10px_rgba(79,70,229,0.5)] transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:scale-100"
			>
				{loading ? (
					<>
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Getting started…
					</>
				) : (
					<>
						Choose {title} <ArrowRight className="h-4 w-4" aria-hidden />
					</>
				)}
			</button>
		</Card>
	);
}
